import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Phone, Search } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { SITE } from "@/lib/site";
import { SearchModal } from "@/components/SearchModal";

const LINKS = [
  { label: "Destinations", to: "/destinations" },
  { label: "Journeys", to: "/packages" },
  { label: "Tours", to: "/tours" },
  { label: "DMC", to: "/northeast-dmc" },
  { label: "Partners", to: "/partners" },
  { label: "Our Story", to: "/about" },
  { label: "Journal", to: "/journal" },
  { label: "Contact", to: "/contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <>
      <header
        data-testid="site-navbar"
        className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,padding] duration-500 ${
          scrolled || open
            ? "bg-[var(--tx-bg)]/80 backdrop-blur-xl border-b border-white/10 py-4"
            : "bg-transparent py-6"
        }`}
      >
        {!scrolled && !open && (
          <div
            aria-hidden="true"
            data-testid="nav-scrim"
            className="pointer-events-none absolute inset-x-0 top-0 h-[220%] bg-gradient-to-b from-black/60 via-black/25 to-transparent"
          />
        )}
        <div className="relative mx-auto max-w-[1400px] px-6 md:px-10 flex items-center justify-between">
          <Link
            to="/"
            data-testid="nav-logo"
            className="flex flex-col leading-none"
          >
            <span className="font-display text-2xl md:text-[1.7rem] font-medium tracking-tight text-[var(--tx-text)]">
              TripTrix
            </span>
            <span className="text-[0.6rem] uppercase tracking-[0.34em] text-[var(--tx-gold)]">
              Voyages
            </span>
            <span className="hidden xl:block text-[0.52rem] tracking-[0.1em] text-[var(--tx-text-2)]" data-testid="nav-parent-company">
              A unit of {SITE.parentCompany}
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-9">
            {LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.to.replace("/", "")}`}
                className={`relative text-sm tracking-wide transition-colors duration-300 hover:text-[var(--tx-gold)] ${
                  location.pathname === l.to
                    ? "text-[var(--tx-gold)]"
                    : "text-[var(--tx-text)]/80"
                }`}
              >
                {l.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <button
              onClick={() => setSearchOpen(true)}
              data-testid="nav-search"
              aria-label="Search"
              className="inline-flex items-center justify-center h-9 w-9 rounded-full text-[var(--tx-text)]/80 hover:text-[var(--tx-gold)] hover:bg-white/5 transition-colors duration-300"
            >
              <Search className="h-[1.15rem] w-[1.15rem]" />
            </button>
            <a
              href={`tel:${SITE.phoneRaw}`}
              data-testid="nav-phone"
              className="hidden md:inline-flex items-center gap-2 text-sm text-[var(--tx-text)]/80 hover:text-[var(--tx-gold)] transition-colors duration-300"
            >
              <Phone className="h-4 w-4" />
              {SITE.phoneDisplay}
            </a>
            <button
              onClick={() => navigate("/contact")}
              data-testid="nav-plan-trip"
              className="hidden sm:inline-flex items-center rounded-full border border-[var(--tx-gold)] px-5 py-2 text-sm text-[var(--tx-gold)] hover:bg-[var(--tx-gold)] hover:text-[var(--tx-bg)] transition-colors duration-300"
            >
              Plan a Trip
            </button>
            <button
              className="lg:hidden text-[var(--tx-text)] p-1"
              onClick={() => setOpen((v) => !v)}
              data-testid="nav-menu-toggle"
              aria-label="Toggle menu"
            >
              {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-x-0 top-[64px] z-40 lg:hidden bg-[var(--tx-bg)]/95 backdrop-blur-xl border-b border-white/10"
            data-testid="mobile-menu"
          >
            <nav className="flex flex-col px-6 py-6">
              <button
                onClick={() => { setOpen(false); setSearchOpen(true); }}
                data-testid="mobile-search"
                aria-label="Search everything"
                className="mb-6 flex w-full items-center gap-3 rounded-full border border-white/15 bg-white/5 px-5 py-3.5 text-left text-[var(--tx-text)]/70 transition-colors duration-300 hover:border-[var(--tx-gold)] hover:text-[var(--tx-gold)]"
              >
                <Search className="h-[1.15rem] w-[1.15rem] shrink-0" />
                <span className="text-base">Search destinations, tours, guides…</span>
              </button>
              {LINKS.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  className="py-4 border-b border-white/5 font-display text-2xl text-[var(--tx-text)]"
                >
                  {l.label}
                </Link>
              ))}
              <a
                href={`tel:${SITE.phoneRaw}`}
                className="mt-5 inline-flex items-center gap-2 text-[var(--tx-gold)]"
              >
                <Phone className="h-4 w-4" /> {SITE.phoneDisplay}
              </a>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>

      <SearchModal open={searchOpen} onClose={() => setSearchOpen(false)} />
    </>
  );
}
